/**
 * 엑셀(blob) 다운로드 응답 처리. (KU-5f, 2026-09-10)
 *
 * ### 왜 분리했나
 *
 * 종전에는 `Users.js` 안에서 응답을 **성공이라고 가정하고** 바로 파일로 만들었다.
 *
 * ```js
 * downloadUsersWorkbook(...).then((res) => {
 *   const contentDisposition = res.headers['content-disposition']   // 실패 시 undefined
 *   const fileName = contentDisposition ? ... : 'downloaded_file.xlsx'
 *   const url = window.URL.createObjectURL(new Blob([res.data]))    // res.data 가 undefined 여도 통과
 *   ...link.click()
 * })
 * ```
 *
 * 🔴 `fetch.js` 의 `getBlob` 은 실패를 **reject 하지 않는다.** `checkAuthorization` 이
 * axios 에러 객체를 그대로 돌려주므로 `res.data` 가 `undefined` 가 되고,
 * `new Blob([undefined])` 는 문자열 `"undefined"` 가 담긴 **9바이트 파일**을 만든다.
 * ⇒ 다운로드가 실패했는데 사용자는 `downloaded_file.xlsx` 를 받아 **성공한 줄 안다.**
 * 열어보면 깨져 있고, 원인은 아무 데도 안 남는다.
 * (같은 계열의 사고 이력: 403 을 받고도 "완료되었습니다" 를 띄웠던 AI 일괄 세팅. `fetch.js` §403 주석)
 *
 * 판정과 파일명 파싱을 여기로 뽑아 테스트로 고정한다. DOM 조작은 `saveBlobResponse` 에만 있다.
 */

/** 실패 시 사용자에게 보여줄 문구. 호출부가 제각각 쓰지 않도록 한 곳에 둔다. */
export const DOWNLOAD_FAILED_MESSAGE =
    "다운로드에 실패했습니다.\n잠시 후 다시 시도하시고, 계속 실패하면 관리자에게 문의해 주세요.";

/**
 * 이 응답이 **실제 파일**인가.
 *
 * `responseType: "blob"` 이므로 성공하면 `res.data` 는 항상 `Blob` 이다.
 * 실패 경로(axios 에러 객체 통과)는 `data` 가 `undefined` 라 여기서 걸린다.
 * 크기 0 도 실패로 본다 — 빈 파일을 주는 것은 아무것도 안 주는 것보다 나쁘다.
 */
export const isDownloadableBlobResponse = (res) =>
    typeof Blob !== "undefined" && res?.data instanceof Blob && res.data.size > 0;

/**
 * `content-disposition` 에서 파일명을 뽑는다.
 *
 * 백엔드는 `attachment; filename=omoplay_users_09_10_14_00_00.xlsx` 형태로 보낸다
 * (`KakaoUserController.excelDownload`). 따옴표가 붙는 서버·프록시도 있어 함께 벗긴다.
 * 헤더가 없거나 형태가 다르면 `fallback` 을 쓴다 — 파일명 때문에 다운로드를 실패시키지 않는다.
 */
export const resolveDownloadFileName = (contentDisposition, fallback = "download.xlsx") => {
    if (typeof contentDisposition !== "string") return fallback;

    const matched = contentDisposition.split("filename=")[1];
    if (!matched) return fallback;

    const fileName = matched.split(";")[0].trim().replace(/^["']|["']$/g, "");
    return fileName || fallback;
};

/**
 * 응답을 파일로 저장한다. **성공 응답만 넘길 것** (`isDownloadableBlobResponse` 로 먼저 판정).
 *
 * `createObjectURL` 로 만든 URL 은 `revokeObjectURL` 로 풀어준다 — 안 풀면 그 blob 이
 * 탭이 살아 있는 동안 메모리에 남는다. 큰 채널은 한 번에 수만 행이라 무시할 크기가 아니다.
 * (종전 코드에는 revoke 가 없었다.)
 */
export const saveBlobResponse = (res, fallbackFileName) => {
    const fileName = resolveDownloadFileName(res?.headers?.["content-disposition"], fallbackFileName);
    const url = window.URL.createObjectURL(res.data);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
    return fileName;
};
